import { MedplumClient } from '@medplum/core';
import { fetchBot } from '@/services/services';

/**
 * Medplum stops a bot at its timeout (10 s by default; 60 s for `create-treatment-payment`, BAR-3051)
 * and `fetchBot` surfaces it as `… Task timed out after N seconds`.
 * A cold start on the services behind the bot is the usual cause (BAR-3125).
 */
export function isBotTimeout(error: unknown): boolean {
  return error instanceof Error && /timed out/i.test(error.message);
}

/**
 * A read through a bot, tried once more when the first attempt timed out: the second usually lands
 * on a warm service. Reads only — a write is not safe to send twice.
 */
export async function fetchBotRead<T>(medplum: MedplumClient, botName: string, input: object): Promise<T> {
  try {
    return await fetchBot<T>(medplum, botName, input);
  } catch (error) {
    if (!isBotTimeout(error)) {
      throw error;
    }
    return fetchBot<T>(medplum, botName, input);
  }
}

/** What a timed-out write reports: it can still land, so it must not read as "nothing happened". */
export const BOT_WRITE_TIMED_OUT =
  'The request timed out. It may still go through; the list will refresh again shortly.';

/**
 * How long after a timed-out write to read the list a second time. On staging a Mark paid that timed
 * out at 10 s landed about 9 s later (BAR-3125): Medplum stops the bot, not its call to the backend,
 * which can outlive any bot timeout.
 */
export const BOT_WRITE_SETTLE_MS = 15_000;

/**
 * A write through a bot. It is never retried. A failure re-reads the list at once, and a timeout
 * re-reads it again once the write has had time to land, so a late success still shows.
 */
export async function fetchBotWrite<T>(
  medplum: MedplumClient,
  botName: string,
  input: object,
  reload: () => void
): Promise<T> {
  try {
    return await fetchBot<T>(medplum, botName, input);
  } catch (error) {
    reload();
    if (isBotTimeout(error)) {
      setTimeout(reload, BOT_WRITE_SETTLE_MS);
      throw new Error(BOT_WRITE_TIMED_OUT);
    }
    throw error;
  }
}
